import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { playChime, useAlarmSound } from "../admin/modules/bookings/SilenceAlerts";
import { getUrgentChats } from "../services/whatsappChatApi";

// A plain check every 30 seconds, not a live stream: another open stream on each portal tab would
// use up the browser's few connections to the server.
const POLL_MS = 30_000;
const SOUNDED_KEY = "chatUrgentSounded";
const NONE = { count: 0, newestAt: null };
const TOKEN_KEYS = { CALL: "callTeamToken", CHAT: "chatTeamToken", ADMIN: "adminToken" };
const CHAT_PATHS = { CALL: "/call-team/chats", CHAT: "/chat-team/chats", ADMIN: "/admin/chats" };

const readToken = (scope) => {
  try {
    return localStorage.getItem(TOKEN_KEYS[scope]) || "";
  } catch {
    return "";
  }
};

/**
 * How many chats need quick attention. Chimes once per browser for each new one, whichever tab sees
 * it first. Admin gets the count but no sound: admin can't reply, and reading a chat doesn't clear it.
 */
export function useUrgentChats(scope, enabled = true) {
  const [summary, setSummary] = useState(NONE);

  const refresh = useCallback(async () => {
    const token = readToken(scope);
    if (!enabled || !token) return;
    try {
      setSummary((await getUrgentChats(scope, token)) || NONE);
    } catch {
      // The next check tries again; what is on screen stays.
    }
  }, [scope, enabled]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const forSound = useMemo(
    () => (scope !== "ADMIN" && summary.count > 0 && summary.newestAt ? [{ id: summary.newestAt }] : []),
    [scope, summary]
  );
  const { soundBlocked } = useAlarmSound(forSound, SOUNDED_KEY, playChime);

  return { count: summary.count, refresh, soundBlocked };
}

/** The portals' way into Chats. Turns red with the number of chats needing quick attention. */
export default function ChatsButton({ scope, size }) {
  const { count } = useUrgentChats(scope);
  return (
    <Button
      size={size}
      variant={count > 0 ? "danger" : "outline-success"}
      onClick={() => window.open(CHAT_PATHS[scope], "_blank", "noopener,noreferrer")}
      title={count > 0 ? `${count} chat${count === 1 ? "" : "s"} need quick attention` : undefined}
    >
      Chats{count > 0 && <Badge bg="light" text="danger" className="ms-2">{count}</Badge>}
    </Button>
  );
}
